import { useState, useEffect, useCallback, useRef } from 'react';
import API from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Lightbulb, X, Sparkle, ShieldCheck, Wrench, Palette,
  Lightning, Rocket, ArrowRight, DownloadSimple, SpinnerGap,
  FileText, Star, CheckCircle, Cube, User, ChartBar, Plugs, List
} from '@phosphor-icons/react';

const CATEGORY_CONFIG = {
  feature:    { icon: Rocket,      color: 'text-orange-600', bg: 'bg-orange-50',  label: 'Feature' },
  ux:         { icon: Palette,     color: 'text-purple-600', bg: 'bg-purple-50',  label: 'UX' },
  security:   { icon: ShieldCheck, color: 'text-emerald-600',bg: 'bg-emerald-50', label: 'Security' },
  automation: { icon: Lightning,   color: 'text-blue-600',   bg: 'bg-blue-50',    label: 'Automation' },
  quality:    { icon: Wrench,      color: 'text-navy-600',   bg: 'bg-navy-50',    label: 'Quality' },
};

const FEATURE_CAT_ICONS = {
  'Identity Management': User,
  'Role & Entitlement Management': ShieldCheck,
  'Access Requests & Approvals': CheckCircle,
  'Compliance & Security': ShieldCheck,
  'Lifecycle & Automation': Lightning,
  'Dashboards & Analytics': ChartBar,
  'Navigation & UX': Palette,
  'Integration & Admin': Plugs,
};

function UpdateItem({ update, isNew }) {
  const cfg = CATEGORY_CONFIG[update.category] || CATEGORY_CONFIG.feature;
  const Icon = cfg.icon;
  return (
    <div className={`px-4 py-3 border-b border-slate-50 last:border-0 transition-colors ${isNew ? 'bg-amber-50/40' : ''}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
          <Icon size={16} weight="duotone" className={cfg.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-navy-900">{update.title}</p>
            {isNew && <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0 shrink-0">NEW</Badge>}
          </div>
          <p className="text-xs text-navy-500 mt-0.5 leading-relaxed">{update.description}</p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className={`text-[10px] font-semibold uppercase ${cfg.color}`}>{cfg.label}</span>
            <span className="text-[10px] text-navy-300">{update.date}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductUpdatesBulb() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ updates: [], new_count: 0 });
  const [showAllDialog, setShowAllDialog] = useState(false);
  const [features, setFeatures] = useState({ categories: [], total_features: 0 });
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [dialogTab, setDialogTab] = useState('features');
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await API.get('/product-updates');
      setData(res.data);
    } catch { /* silent */ }
  }, []);

  const loadFeatures = useCallback(async () => {
    try {
      const res = await API.get('/user-guide/features');
      setFeatures(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = async () => {
    const wasOpen = open;
    setOpen(!wasOpen);
    if (!wasOpen) {
      load();
      try {
        await API.post('/product-updates/mark-viewed');
        setTimeout(() => {
          setData(prev => ({ ...prev, new_count: 0, last_viewed: new Date().toISOString().slice(0, 10) }));
        }, 500);
      } catch { /* silent */ }
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    loadFeatures();
    setShowAllDialog(true);
  };

  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    try {
      const guideRes = await API.get('/user-guide');
      const guide = guideRes.data;

      // Fetch screenshots as base64
      const screenshotNames = ['login', 'my_identity', 'dashboard', 'identities', 'roles', 'access_requests', 'approvals', 'global_search'];
      const screenshots = {};
      await Promise.all(screenshotNames.map(async (name) => {
        try {
          const resp = await fetch(`${API.defaults.baseURL}/static/guide_screenshots/${name}.jpeg`, { credentials: 'include' });
          if (resp.ok) {
            const blob = await resp.blob();
            const reader = new FileReader();
            const b64 = await new Promise((resolve) => {
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
            screenshots[name] = b64;
          }
        } catch { /* skip */ }
      }));

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = doc.internal.pageSize.getWidth();
      const margin = 18;
      const contentW = W - margin * 2;
      let y = 0;

      const addPage = () => { doc.addPage(); y = margin; };
      const checkPage = (needed) => { if (y + needed > 272) addPage(); };
      const orangeBar = () => { doc.setFillColor(249, 115, 22); doc.rect(margin, y, 35, 1.2, 'F'); y += 6; };
      const sectionTitle = (text) => {
        orangeBar();
        doc.setFont('helvetica', 'bold'); doc.setFontSize(17); doc.setTextColor(15, 23, 42);
        doc.text(text, margin, y); y += 10;
      };
      const subHeading = (text) => {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(30, 41, 59);
        doc.text(text, margin, y); y += 5.5;
      };
      const bodyText = (text) => {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(71, 85, 105);
        const lines = doc.splitTextToSize(text, contentW);
        lines.forEach(line => { checkPage(5); doc.text(line, margin, y); y += 4.2; });
      };
      const stepList = (steps) => {
        steps.forEach((step, i) => {
          checkPage(6);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(249, 115, 22);
          doc.text(`${i + 1}.`, margin + 2, y);
          doc.setFont('helvetica', 'normal'); doc.setTextColor(51, 65, 85);
          const lines = doc.splitTextToSize(step, contentW - 10);
          lines.forEach((line, li) => {
            if (li > 0) checkPage(4.5);
            doc.text(line, margin + 9, y);
            y += 4.2;
          });
          y += 1;
        });
      };
      const addScreenshot = (name) => {
        if (!screenshots[name]) return;
        checkPage(65);
        // Draw border
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, contentW, 58, 2, 2, 'S');
        try {
          doc.addImage(screenshots[name], 'JPEG', margin + 0.5, y + 0.5, contentW - 1, 57, undefined, 'FAST');
        } catch { /* skip bad image */ }
        y += 62;
      };

      // --- Cover Page ---
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, W, 297, 'F');
      doc.setFillColor(249, 115, 22);
      doc.rect(0, 115, W, 3, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(36); doc.setTextColor(255, 255, 255);
      doc.text('IAG Platform', margin, 75);
      doc.setFontSize(18); doc.setTextColor(203, 213, 225);
      doc.text('User Guide', margin, 92);
      doc.setFontSize(11); doc.setTextColor(148, 163, 184);
      doc.text(`Version ${guide.version}  |  ${guide.date}`, margin, 140);
      doc.text('Identity Access Governance', margin, 150);
      doc.setFontSize(9);
      doc.text('Complete step-by-step guide with screenshots', margin, 165);

      // --- Table of Contents ---
      addPage();
      sectionTitle('Table of Contents');
      guide.sections.forEach((section, idx) => {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(15, 23, 42);
        doc.text(`${idx + 1}.  ${section.title}`, margin + 4, y);
        y += 7;
      });
      y += 4;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(15, 23, 42);
      doc.text(`${guide.sections.length + 1}.  Recent Updates`, margin + 4, y);

      // --- Sections with screenshots and steps ---
      guide.sections.forEach((section, sIdx) => {
        addPage();
        sectionTitle(`${sIdx + 1}. ${section.title}`);

        // Add screenshot for this section if available
        if (section.screenshot) {
          addScreenshot(section.screenshot);
          y += 3;
        }

        section.items.forEach((item) => {
          checkPage(20);
          subHeading(item.heading);
          bodyText(item.text);
          if (item.steps && item.steps.length > 0) {
            y += 2;
            checkPage(10);
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(15, 23, 42);
            doc.text('Steps:', margin, y); y += 5;
            stepList(item.steps);
          }
          y += 4;
        });
      });

      // --- Changelog ---
      addPage();
      sectionTitle(`${guide.sections.length + 1}. Recent Updates`);
      data.updates.forEach((update) => {
        checkPage(16);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(15, 23, 42);
        doc.text(update.title, margin, y);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(148, 163, 184);
        doc.text(`${update.category.toUpperCase()}  |  ${update.date}`, margin + doc.getTextWidth(update.title) + 3, y);
        y += 4.5;
        bodyText(update.description);
        y += 3;
      });

      // --- Page footers ---
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 2; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(226, 232, 240); doc.line(margin, 286, W - margin, 286);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
        doc.text('IAG Platform User Guide', margin, 291);
        doc.text(`Page ${i - 1} of ${pageCount - 1}`, W - margin - 22, 291);
      }

      doc.save('IAG_Platform_User_Guide.pdf');
    } catch (err) {
      void 0;
    } finally {
      setGeneratingPdf(false);
    }
  };

  const newCount = data.new_count;

  return (
    <>
      <div className="relative" ref={ref}>
        <button data-testid="product-updates-bulb" onClick={handleOpen} className="relative p-2 rounded-md hover:bg-slate-100 transition-colors">
          <Lightbulb size={20} weight={open ? 'fill' : 'regular'} className={open ? 'text-amber-500' : 'text-navy-600'} />
          {newCount > 0 && (
            <span data-testid="product-updates-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold px-1 animate-scale-in">
              {newCount > 99 ? '99+' : newCount}
            </span>
          )}
        </button>

        {open && (
          <div data-testid="product-updates-dropdown" className="absolute right-0 top-full mt-2 w-[400px] bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden animate-fade-in">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white">
              <div className="flex items-center gap-2">
                <Sparkle size={16} weight="fill" className="text-amber-500" />
                <h3 className="text-sm font-bold text-navy-900">What's New</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded"><X size={14} className="text-navy-400" /></button>
            </div>
            <div className="max-h-[320px] overflow-y-auto" data-testid="product-updates-list">
              {data.updates.slice(0, 5).map((update) => (
                <UpdateItem key={update.id} update={update} isNew={data.last_viewed ? update.date > data.last_viewed : true} />
              ))}
            </div>
            <div className="border-t border-slate-100 px-3 py-2.5 flex items-center justify-between gap-2">
              <Button variant="ghost" size="sm" className="text-xs text-navy-600 hover:text-orange-600 h-7 px-2" onClick={handleViewAll} data-testid="view-all-updates-btn">
                <List size={12} className="mr-1" /> All Features <ArrowRight size={12} className="ml-1" />
              </Button>
              <Button variant="ghost" size="sm" className="text-xs text-navy-600 hover:text-orange-600 h-7 px-2" onClick={handleDownloadPdf} disabled={generatingPdf} data-testid="download-user-guide-btn">
                {generatingPdf ? <SpinnerGap size={12} className="mr-1 animate-spin" /> : <DownloadSimple size={12} className="mr-1" />}
                User Guide PDF
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* All Features Dialog */}
      <Dialog open={showAllDialog} onOpenChange={setShowAllDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="all-features-dialog" aria-describedby="all-features-desc">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Star size={20} weight="fill" className="text-amber-500" />
              IAG Platform
            </DialogTitle>
          </DialogHeader>

          <Tabs value={dialogTab} onValueChange={setDialogTab} className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between shrink-0 pb-2">
              <TabsList className="bg-slate-100 rounded-sm p-0.5">
                <TabsTrigger value="features" className="rounded-sm text-xs px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-features">
                  <Star size={12} className="mr-1" /> All Features ({features.total_features})
                </TabsTrigger>
                <TabsTrigger value="updates" className="rounded-sm text-xs px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-updates">
                  <Sparkle size={12} className="mr-1" /> Recent Updates ({data.updates.length})
                </TabsTrigger>
              </TabsList>
              <Button variant="outline" size="sm" className="rounded-sm text-xs h-8" onClick={handleDownloadPdf} disabled={generatingPdf} data-testid="dialog-download-pdf-btn">
                {generatingPdf ? <SpinnerGap size={14} className="mr-1.5 animate-spin" /> : <FileText size={14} className="mr-1.5" />}
                Download User Guide PDF
              </Button>
            </div>

            {/* Features Tab */}
            <TabsContent value="features" className="flex-1 overflow-y-auto border border-slate-200 rounded-sm mt-0" data-testid="all-features-list">
              <p id="all-features-desc" className="px-4 py-2 text-xs text-navy-400 border-b border-slate-100">
                {features.total_features} features across {features.categories.length} categories
              </p>
              {features.categories.map((cat) => {
                const CatIcon = FEATURE_CAT_ICONS[cat.category] || Star;
                return (
                  <div key={cat.category}>
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2 sticky top-0 z-10">
                      <CatIcon size={14} weight="duotone" className="text-orange-500" />
                      <span className="text-xs font-bold text-navy-900 uppercase tracking-widest">{cat.category}</span>
                      <Badge className="bg-navy-100 text-navy-600 text-[9px] ml-auto">{cat.features.length}</Badge>
                    </div>
                    {cat.features.map((feat) => (
                      <div key={feat.name} className="px-4 py-2.5 border-b border-slate-50 flex items-start gap-3 hover:bg-slate-50/50 transition-colors">
                        <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-navy-900">{feat.name}</p>
                          <p className="text-xs text-navy-500 mt-0.5">{feat.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </TabsContent>

            {/* Updates Tab */}
            <TabsContent value="updates" className="flex-1 overflow-y-auto border border-slate-200 rounded-sm mt-0">
              {data.updates.map((update) => (
                <UpdateItem key={update.id} update={update} isNew={data.last_viewed ? update.date > data.last_viewed : true} />
              ))}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
